// src/component/PaymentPhases2026.js
import React, { useEffect, useState, useContext } from "react";
import {
  doc,
  onSnapshot,
  getDocs,
  collection,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import StripeCheckoutButton2025 from "./StripeCheckoutButton2025";
import { db, auth } from "../api/firebase";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
  TextField,
  Collapse,
  Stack,
} from "@mui/material";

import CelebrationIcon from "@mui/icons-material/Celebration";
import LocalActivityRoundedIcon from "@mui/icons-material/LocalActivityRounded";
import { AppThemeContext } from "./AppShell";

// ひらがな → カタカナ
const toKatakana = (str = "") => {
  return str.replace(/[\u3041-\u3096]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) + 0x60)
  );
};

// 金額表示用フォーマッタ
const formatAmount = (value) => {
  if (value == null) return "-";

  if (typeof value === "number") {
    return `¥${value.toLocaleString()}`;
  }

  if (typeof value === "string") {
    // すでに「¥28,000」などならそのまま
    if (value.includes("¥")) return value;

    const n = Number(value.replace(/,/g, ""));
    if (!Number.isNaN(n)) {
      return `¥${n.toLocaleString()}`;
    }
    return value;
  }

  return String(value);
};

export default function PaymentPhases2026({
  externalCoupon = null,
  onRemoveExternalCoupon = null,
} = {}) {
  // ★ 2026 の支払いフェーズ番号
  const paymentPhase = "4";

  const title = "マスターマインドメンバーズ2026";
  const description = "2026年度のマスターマインド正式参加申し込み";

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [rangeLoading, setRangeLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [dateRanges, setDateRanges] = useState([]);

  const [hasBankTransferRequest, setHasBankTransferRequest] = useState(false);
  const [bankTransferRequestData, setBankTransferRequestData] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [isBankTransferExpired, setBankTransferExpired] = useState(false);
  const [expiredDeadlineDate, setExpiredDeadlineDate] = useState(null);

  // ─── クーポン関連（内部入力UI用） ───
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [internalAppliedCoupon, setAppliedCoupon] = useState(null); // { code, discountedPrice, stripePromotionCodeId }
  // 外部から渡された場合はそちらを優先
  const appliedCoupon = externalCoupon || internalAppliedCoupon;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError("");

    try {
      const { getDocs: gd, query: q, where: w } = await import("firebase/firestore");
      const snap = await gd(
        q(
          collection(db, "coupons"),
          w("code", "==", couponCode.trim().toUpperCase()),
          w("active", "==", true)
        )
      );

      if (snap.empty) {
        setCouponError("無効なクーポンコードです");
        setCouponLoading(false);
        return;
      }

      const couponData = snap.docs[0].data();
      const now_ = Date.now();
      const start = couponData.start?.toMillis?.() ?? 0;
      const end = couponData.end?.toMillis?.() ?? Infinity;

      if (now_ < start) {
        setCouponError("このクーポンはまだ有効期間外です");
        setCouponLoading(false);
        return;
      }
      if (now_ > end) {
        setCouponError("このクーポンは有効期限が切れています");
        setCouponLoading(false);
        return;
      }
      if (couponData.maxUses && couponData.usedCount >= couponData.maxUses) {
        setCouponError("このクーポンは利用上限に達しています");
        setCouponLoading(false);
        return;
      }

      setAppliedCoupon({
        id: snap.docs[0].id,
        code: couponData.code,
        discountedPrice: couponData.discountedPrice,
        stripePromotionCodeId: couponData.stripePromotionCodeId,
      });
      setCouponError("");
    } catch (err) {
      console.error("クーポン検証エラー:", err);
      setCouponError("エラーが発生しました。もう一度お試しください");
    }
    setCouponLoading(false);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    // 外部クーポン (CouponPaymentBox 由来) も解除
    if (typeof onRemoveExternalCoupon === "function") {
      onRemoveExternalCoupon();
    }
  };

  // 🌈 AppShell テーマ
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const textBase = theme.railText || "rgba(30,30,60,0.92)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  // 10秒ごとに時刻更新
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(id);
  }, []);

  // ユーザーデータ取得
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, "users", currentUser.uid);

    const unsub = onSnapshot(
      userDocRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setUserData({ ...data, userId: currentUser.uid });
        }
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // フェーズ情報の取得
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "paymentPhases2026"),
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            start: data.start.toDate(),
            end: data.end ? data.end.toDate() : null,
          };
        });

        setDateRanges(list);
        setRangeLoading(false);
      },
      (err) => {
        console.error("支払フェーズ取得エラー:", err);
        setRangeLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // 銀行振込チェック
  useEffect(() => {
    checkBankTransferRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkBankTransferRequest = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const q = query(
      collection(db, "bankTransferRequests2026"),
      where("userId", "==", currentUser.uid)
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      setHasBankTransferRequest(false);
      setBankTransferRequestData(null);
      setBankTransferExpired(false);
      setExpiredDeadlineDate(null);
      return;
    }

    const sorted = snap.docs
      .filter((d) => d.data().requestedAt)
      .sort(
        (a, b) => b.data().requestedAt.toDate() - a.data().requestedAt.toDate()
      );

    const latest = sorted[0].data();
    const requestedAt = latest.requestedAt.toDate();

    const deadline = new Date(requestedAt);
    deadline.setDate(deadline.getDate() + 7);

    const expired = now > deadline;

    if (latest.cancel) {
      setHasBankTransferRequest(false);
      setBankTransferRequestData(null);
      setBankTransferExpired(false);
      setExpiredDeadlineDate(null);
      return;
    }

    if (expired) {
      setHasBankTransferRequest(false);
      setBankTransferRequestData(null);
      setBankTransferExpired(true);
      setExpiredDeadlineDate(deadline);
      return;
    }

    setHasBankTransferRequest(true);
    setBankTransferRequestData(latest);
    setBankTransferExpired(false);
    setExpiredDeadlineDate(null);
  };

  const handleCancelBankTransfer = async () => {
    try {
      const q = query(
        collection(db, "bankTransferRequests2026"),
        where("userId", "==", auth.currentUser.uid)
      );

      const snap = await getDocs(q);
      const valid = snap.docs
        .map((d) => ({ id: d.id, ref: d.ref, ...d.data() }))
        .filter((d) => !d.cancel && d.requestedAt)
        .sort((a, b) => b.requestedAt.toDate() - a.requestedAt.toDate());

      if (!valid.length) return;

      await updateDoc(valid[0].ref, {
        cancel: true,
        canceledAt: serverTimestamp(),
      });

      alert("銀行振込申請をキャンセルしました。");
      setCancelDialogOpen(false);
      setCancelConfirmed(false);
      setHasBankTransferRequest(false);
    } catch (err) {
      console.error(err);
      alert("エラーが発生しました");
    }
  };

  // 支払い済みかどうか
  const payment = userData?.payments?.find(
    (p) => p.paymentPhase === paymentPhase
  );
  const method = payment?.paymentMethod;

  // ==============================
  // 支払い済み（銀行振込 or カード）
  // ==============================
  if (method === "銀行振込" || method === "クレジットカード") {
    return (
      <Box
        sx={{
          mt: 4,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${accent}10, ${accent}24)`,
          border: `1px solid ${accent}40`,
          boxShadow: "0 14px 40px rgba(0,0,0,0.12)",
          textAlign: "center",
          maxWidth: 680,
          mx: "auto",
        }}
      >
        <CelebrationIcon
          sx={{
            fontSize: 44,
            color: accent,
            mb: 1,
            display: "block",
            mx: "auto",
          }}
        />
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.26em",
            fontSize: 11,
            opacity: 0.85,
            color: textBase,
          }}
        >
          ENTRY CONFIRMED
        </Typography>
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ color: textBase, mt: 0.5 }}
        >
          2026年度マスターマインドへの参加が確定しました。
        </Typography>

        <Typography
          variant="subtitle2"
          sx={{
            mt: 1,
            mb: 2,
            color: accent,
            letterSpacing: "0.08em",
          }}
        >
          あなたは 2026 年のマスターマインドの「中心メンバー」です。
        </Typography>

        <Divider sx={{ my: 2.2, opacity: 0.7 }} />

        <Typography
          variant="body1"
          sx={{ color: textBase, lineHeight: 1.8, mb: 1.5 }}
        >
          これから一年、少人数グループやオンライン企画を通して、
          自分のテーマに静かに向き合いながら、
          同じ想いを持つ仲間たちと歩んでいきます。
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(0,0,0,0.7)",
            lineHeight: 1.7,
          }}
        >
          開催スケジュールや具体的なプログラム内容は、
          メンバー専用ページ・メール等で随時ご案内します。
        </Typography>
      </Box>
    );
  }

  // ローディング
  if (loading || rangeLoading)
    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (!userData) return <div>ユーザー情報を取得できませんでした。</div>;

  const isOverseas = userData.prefecture?.includes("海外");
  const hasPayment = Boolean(payment);

  // 追加
  const taxLabel = isOverseas ? "(税抜き)" : "(税込み)";

  const formattedPhone = userData.registrationPhone
    ? userData.registrationPhone.slice(-4)
    : "----";

  // ==============================
  // 銀行振込 受付中画面
  // ==============================
  if (hasBankTransferRequest && bankTransferRequestData?.requestedAt) {
    const reqDate = bankTransferRequestData.requestedAt.toDate();
    const deadline = new Date(reqDate);
    deadline.setDate(deadline.getDate() + 7);

    const formatDate = (d) =>
      d.toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    // ▼ 振込依頼人名の表示用（カタカナ）
    const registrationFuriganaKatakana = userData.registrationFurigana
      ? toKatakana(userData.registrationFurigana)
      : "";

    const transferDisplayName =
      formattedPhone &&
      (registrationFuriganaKatakana || userData.registrationName)
        ? `${formattedPhone}　${
            registrationFuriganaKatakana || userData.registrationName
          }`
        : "";

    return (
      <Box
        sx={{
          mt: 4,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          maxWidth: 640,
          mx: "auto",
          backgroundColor: "rgba(255,250,230,0.96)",
          border: "1px solid rgba(255,193,7,0.6)",
        }}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.2em",
            fontSize: 10,
            color: "rgba(120,90,0,0.9)",
          }}
        >
          BANK TRANSFER PENDING
        </Typography>
        <Typography
          variant="h6"
          color="primary"
          fontWeight="bold"
          sx={{ mb: 1 }}
        >
          銀行振込でのお申込みを受け付けています
        </Typography>

        <Typography variant="body2" sx={{ mt: 1, color: "rgba(80,60,0,0.9)" }}>
          申込日: {formatDate(reqDate)}
        </Typography>

        {/* ▼ 振込先情報 & 依頼人名表示 */}
        <Typography
          variant="body2"
          sx={{
            mt: 1.5,
            color: "rgba(80,60,0,0.9)",
            whiteSpace: "pre-line",
          }}
        >
          {`振込依頼人名に、お申込み時の電話番号の下4桁を入れてお振込みください。
（例：「2134　マスター　タロウ」）

金融機関：GMOあおぞらネット銀行
支店名：法人第二営業部
口座種別：普通
口座番号：1007963
名義：ファミュニティリンク株式会社イベント
入金額: ${formatAmount(bankTransferRequestData.amount)}

お振込依頼人名：${transferDisplayName || "（申込時の電話番号下4桁＋お名前）"}`}
        </Typography>

        <Box
          sx={{
            mt: 2,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            backgroundColor: "rgba(255,214,102,0.4)",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: "bold", color: "rgba(120,70,0,0.95)" }}
          >
            振込期限: {formatDate(deadline)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: "rgba(120,70,0,0.9)" }}
          >
            期限を過ぎますと、この申込みはいったん無効となります。
            再度参加を希望される場合は、もう一度お手続きください。
          </Typography>
        </Box>

        <Box mt={3} textAlign="center">
          <Button
            variant="outlined"
            color="error"
            onClick={() => setCancelDialogOpen(true)}
          >
            銀行振込をキャンセルする
          </Button>
        </Box>

        {/* キャンセルダイアログ */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
        >
          <DialogTitle>銀行振込をキャンセル</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              この操作は取り消せません。本当にキャンセルしますか？
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={cancelConfirmed}
                  onChange={(e) => setCancelConfirmed(e.target.checked)}
                />
              }
              label="上記内容を確認しました"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>閉じる</Button>
            <Button
              variant="contained"
              color="error"
              disabled={!cancelConfirmed}
              onClick={handleCancelBankTransfer}
            >
              キャンセルする
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ==============================
  // 本来の支払いボックス一覧
  // ==============================
  return (
    <Box sx={{ mt: 3 }}>
      {/* セクションヘッダー */}

      {/* 期限切れ自動キャンセルの表示（もしあれば） */}
      {isBankTransferExpired && expiredDeadlineDate && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "#fff3e0",
            borderLeft: "6px solid #ff9800",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: "bold", color: "#e65100" }}
          >
            銀行振込の申請は期限切れとなりました
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            前回の銀行振込申込みは振込期限を過ぎたため、自動的にキャンセルされています。
            あらためて 2026
            参加をご希望の場合は、下記のいずれかのプランから再度お申し込みください。
          </Typography>
        </Box>
      )}

      {/* プランカード群 */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center",
        }}
      >
        {dateRanges
          .filter((range) => {
            // adminOnly が ON: 管理者だけ表示（visible 設定を無視）
            if (range.adminOnly) return !!userData?.isAdmin;
            // adminOnly が OFF: visible に従う
            return range.visible !== false;
          })
          .map((range) => {
            const active =
              now >= range.start && (!range.end || now <= range.end);

            const originalPriceNum = isOverseas
              ? Number(range.priceTaxExcluded)
              : Number(range.priceTaxIncluded);
            const price = `¥${originalPriceNum.toLocaleString()}`;

            const priceId = isOverseas
              ? range.priceIdTaxExcluded
              : range.priceIdTaxIncluded;

            // クーポン適用時: 割引率(%)から計算（フェーズ価格に追従）
            let discountedPriceLabel = null;
            const originalDisplayPriceNum = originalPriceNum;
            if (appliedCoupon) {
              const pct = Number(appliedCoupon.discountPercent);
              const validPct =
                Number.isFinite(pct) && pct >= 0 && pct <= 100 ? pct : 0;
              const finalDiscounted = Math.round(
                originalPriceNum * (1 - validPct / 100)
              );
              discountedPriceLabel = `¥${finalDiscounted.toLocaleString()}`;
            }
            const originalDisplayPrice = `¥${originalDisplayPriceNum.toLocaleString()}`;

            const label = range.label || "エントリープラン";
            const startLabel = range.start.toLocaleDateString("ja-JP");
            const endLabel = range.end
              ? range.end.toLocaleDateString("ja-JP")
              : "期限なし";

            return (
              <Box
                key={range.id}
                sx={{
                  maxWidth: 420,
                  minWidth: 260,
                  borderRadius: 3,
                  backgroundColor: surface,
                  textAlign: "center",
                  opacity: active && !hasPayment ? 1 : 0.5,
                  pointerEvents: active && !hasPayment ? "auto" : "none",
                  boxShadow: active
                    ? "0 16px 40px rgba(0,0,0,0.14)"
                    : "0 3px 10px rgba(0,0,0,0.04)",
                  border: `1px solid ${border}`,
                  transition:
                    "opacity .3s, box-shadow .3s, transform .2s, border-color .2s",
                  "&:hover":
                    active && !hasPayment
                      ? {
                          transform: "translateY(-3px)",
                          borderColor: `${accent}55`,
                        }
                      : undefined,
                  p: 2.4,
                }}
              >
                {/* ラベル・期間表示 */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: textBase,
                    mb: 0.2,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(0,0,0,0.6)",
                    letterSpacing: "0.06em",
                  }}
                >
                  {range.isEarlyBird ? "早期受付期間" : "受付期間"}:{" "}
                  {startLabel} 〜 {endLabel}
                </Typography>

                {/* 金額表示 */}
                <Box sx={{ mt: 1.6, mb: 1.4 }}>
                  {appliedCoupon ? (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: "line-through",
                          color: "rgba(0,0,0,0.4)",
                          fontSize: 14,
                        }}
                      >
                        {originalDisplayPrice}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, color: "#e53935" }}
                      >
                        {discountedPriceLabel}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                        sx={{ mt: 0.5 }}
                      >
                        <LocalActivityRoundedIcon
                          sx={{ fontSize: 14, color: "#e53935" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "#e53935", fontWeight: 600 }}
                        >
                          クーポン適用中: {appliedCoupon.code}
                        </Typography>
                        <Typography
                          variant="caption"
                          onClick={handleRemoveCoupon}
                          sx={{
                            color: "rgba(0,0,0,0.5)",
                            cursor: "pointer",
                            textDecoration: "underline",
                            ml: 1,
                          }}
                        >
                          解除
                        </Typography>
                      </Stack>
                    </>
                  ) : (
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: accent }}
                    >
                      {price}
                    </Typography>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "rgba(0,0,0,0.6)",
                      mt: 0.2,
                    }}
                  >
                    {taxLabel}
                  </Typography>
                </Box>

                {/* クーポン入力（外部から渡されていない場合のみ表示） */}
                {!externalCoupon && !appliedCoupon && !hasPayment && (
                  <Box sx={{ mb: 1.5 }}>
                    {!couponOpen ? (
                      <Typography
                        variant="caption"
                        onClick={() => setCouponOpen(true)}
                        sx={{
                          color: "rgba(0,0,0,0.45)",
                          cursor: "pointer",
                          "&:hover": { color: accent },
                          transition: "color .2s",
                        }}
                      >
                        クーポンコードをお持ちですか？
                      </Typography>
                    ) : (
                      <Collapse in={couponOpen}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                          sx={{ mt: 1 }}
                        >
                          <TextField
                            size="small"
                            placeholder="コードを入力"
                            value={couponCode}
                            onChange={(e) =>
                              setCouponCode(e.target.value.toUpperCase())
                            }
                            error={!!couponError}
                            helperText={couponError}
                            sx={{ flex: 1 }}
                            InputProps={{
                              sx: {
                                fontFamily: "monospace",
                                letterSpacing: "0.1em",
                                fontSize: 13,
                              },
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleApplyCoupon}
                            disabled={couponLoading || !couponCode.trim()}
                            sx={{
                              minWidth: 60,
                              borderColor: accent,
                              color: accent,
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                          >
                            {couponLoading ? "..." : "適用"}
                          </Button>
                        </Stack>
                      </Collapse>
                    )}
                  </Box>
                )}

                {/* Stripe ボタン */}
                <StripeCheckoutButton2025
                  userId={userData.userId}
                  priceId={priceId}
                  price={appliedCoupon ? discountedPriceLabel : price}
                  title={title}
                  description={description}
                  paymentPhase={paymentPhase}
                  hasPayment={hasPayment}
                  formattedPhone={formattedPhone}
                  displayStart={startLabel}
                  displayEnd={endLabel}
                  paymentPhaseId={range.id}
                  onBankTransferRequestComplete={checkBankTransferRequest}
                  isEarlyBird={!!range.isEarlyBird}
                  couponCode={appliedCoupon?.code || null}
                  stripePromotionCodeId={appliedCoupon?.stripePromotionCodeId || null}
                  couponId={appliedCoupon?.id || null}
                />
              </Box>
            );
          })}
      </Box>

      
    </Box>
  );
}
